import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { buildSideUnionPath, RAIL_BOTTOM } from './detailsShape'

/**
 * Wie lange die Silhouette den Pillen nachläuft, nachdem sich ihre Zahl
 * geändert hat. Etwas länger als deren Übergang (350ms in `DetailsRail.scss`),
 * damit der letzte Frame sicher noch gezeichnet wird.
 */
const FOLLOW_MS = 400

/**
 * **Die Silhouette zeichnen und sie den Pillen nachführen.**
 *
 * Gemessen statt gerechnet, und der Grund ist die Bewegung: Die Pillen rücken
 * per CSS-Übergang nach, wenn Gruppe 1 wegfällt. Ein zweiter Rechenweg in JS
 * träfe die Zwischenwerte der Kurve nie genau — gelesen werden sie exakt.
 * Deshalb läuft nach jeder Änderung ein `requestAnimationFrame` mit, der die
 * Pillen Bild für Bild abliest.
 *
 * **Im Leerzustand hält der Umriss seine Höhe** (Plan, Entscheidung 10):
 * Steht die Auswahl auf einem Abschnittskopf, verschwinden die Pillen und es
 * gibt nichts zu messen — dann bleibt der letzte Stand stehen. Bis auf null zu
 * atmen hieße, das Zusammenklappen zu starten und 20ms später (so schnell geht
 * eine gehaltene Pfeiltaste durch die Liste) umzukehren; die Bewegung käme nie
 * an, der Umriss zuckte nur.
 *
 * Nullmessungen werden ebenso verworfen: Inaktive Tabs bleiben mit
 * `display: none` montiert und messen nur Nullen (CLAUDE.md).
 */
export function useDetailsShape(
  svgRef: React.RefObject<SVGSVGElement | null>,
  pathRef: React.RefObject<SVGPathElement | null>,
  railRef: React.RefObject<HTMLElement | null>,
  /** Ändert sich, sobald die Leiste andere Pillen trägt — der Anlass zum Mitlaufen. */
  railKey: string,
) {
  /** Die letzte brauchbare Unterkante des Buckels. Trägt den Leerzustand und den inaktiven Tab. */
  const lastBottom = useRef(0)
  const frameRef = useRef(0)

  const apply = useCallback(() => {
    const svg = svgRef.current
    const path = pathRef.current
    if (!svg || !path) return

    const box = svg.getBoundingClientRect()
    const w = Math.round(box.width)
    const h = Math.round(box.height)
    if (w === 0 || h === 0) return

    // Der Buckel reicht bis unter die LETZTE Pille — eine einzige Zahl, nicht
    // eine je Gruppe: Der Entwurf zeigt EINE Ausbuchtung, in der alle drei
    // Pillen liegen (Figma `5279:208021`).
    const pills = railRef.current?.querySelectorAll<HTMLElement>('.details-rail__pill')
    const last = pills && pills.length > 0 ? pills[pills.length - 1] : null
    if (last) {
      const rect = last.getBoundingClientRect()
      lastBottom.current = rect.bottom - box.top + RAIL_BOTTOM
    }

    svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
    path.setAttribute('d', buildSideUnionPath(w, h, lastBottom.current))
  }, [svgRef, pathRef, railRef])

  // Beim Aufbau und bei jeder Größenänderung des Feldes.
  useLayoutEffect(() => {
    apply()
    const svg = svgRef.current
    if (!svg || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(apply)
    observer.observe(svg)
    return () => observer.disconnect()
  }, [apply, svgRef])

  // Und nachlaufen, solange die Pillen sich bewegen.
  useEffect(() => {
    const started = performance.now()
    const tick = () => {
      apply()
      if (performance.now() - started < FOLLOW_MS) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [railKey, apply])
}
